
import { Component } from 'vue-property-decorator';
import AmountDisplayerFilterable from '@/components/reports-v3/components/filterables/AmountDisplayerFilterable.vue';
import moment from 'moment';
import { COL, sqlSum, sqlSumByDocType, getTableName } from '@/worker/db/sqlHelpers';
import FilterWidget from '../shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import { addComma } from '@/util/number';
import gdbx from '@/store/modules/gdbx';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
	components: {
		AmountDisplayerFilterable,
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class SalesActiveCustomerNumberDisplayer extends FilterWidget {
	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = [];

	public customersCount = 0;
	public totalSales: any = '';
	public salesPerCustomer: any = '';

	public get permissionIds(): PermissionsGroup[] {
		return ['sales', 'customers'];
	}

	public get currentYear() {
		return moment(this.selectedAsOfDate).year();
	}

	public get currency() {
		return gdbx.currencySymbol;
	}

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	public get expensiveHook() {
		const {
			selectedDateRange,
			selectedAsOfDate,
			selectedAgents,
			selectedStockItems,
			currentYear,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		} = this;
		return JSON.stringify([
			selectedDateRange,
			selectedAsOfDate,
			selectedAgents,
			selectedStockItems,
			currentYear,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.3);

		const queryHead = await this._ensureTablesLoaded(['sales', 'creditNotes', 'customerTrades'] as any[]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.9);

		const salesTable = getTableName('sales');
		const cnTable = getTableName('creditNotes');
		const arTable = getTableName('customerTrades');
		const yearStart = moment([this.currentYear]).startOf('year').valueOf();
		const yearEnd = moment([this.currentYear]).endOf('year').valueOf();
		const yearFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${yearStart} AND ${yearEnd}`;

		let customersCount = 0;
		let sales = 0;

		if (this.slPhModuleOnly) {
			// ========================================
			// SL Mode - use sales + creditNotes tables
			// ========================================

			const docTypes = this.includeDebitNote ? ['IV', 'CS', 'DN'] : ['IV', 'CS'];
			const docTypeFilter = ` AND ${COL.DOCTYPE} IN (${docTypes.map((t) => `'${t}'`).join(', ')})`;

			const [salesResult, cnResult] = await Promise.all([
				queryHead.rawQuery(`
					SELECT ${sqlSum(COL.AMOUNT, 'totalAmt')},
						COUNT(DISTINCT ${COL.CODE}) as customerCount
					FROM ${salesTable}
					WHERE ${yearFilter}${docTypeFilter}
				`),
				queryHead.rawQuery(`
					SELECT ${sqlSum(COL.AMOUNT, 'totalAmt')}
					FROM ${cnTable}
					WHERE ${yearFilter}
				`),
			]);
			this.checkInterrupted(jobId);

			const salesAmount = Number(salesResult[0] && salesResult[0].totalAmt) || 0;
			customersCount = Number(salesResult[0] && salesResult[0].customerCount) || 0;
			const cnAmount = this.includeCreditNote
				? (Number(cnResult[0] && cnResult[0].totalAmt) || 0)
				: 0;
			sales = salesAmount - cnAmount;
		} else {
			// ========================================
			// AR Mode - use customerTrades table
			// ========================================

			const arResult = await queryHead.rawQuery(`
				SELECT
					${sqlSumByDocType(['IV'], COL.LOCALAMOUNT, 'iv_amount')},
					${sqlSumByDocType(['DN'], COL.LOCALAMOUNT, 'dn_amount')},
					${sqlSumByDocType(['CN'], COL.LOCALAMOUNT, 'cn_amount')},
					COUNT(DISTINCT CASE WHEN ${COL.DOCTYPE} = 'IV' THEN ${COL.CODE} END) as iv_customer_count,
					COUNT(DISTINCT CASE WHEN ${COL.DOCTYPE} IN ('IV', 'DN') THEN ${COL.CODE} END) as iv_dn_customer_count
				FROM ${arTable}
				WHERE ${yearFilter}
			`);
			this.checkInterrupted(jobId);

			const row = arResult[0] || {};
			const ivAmt = Number(row.iv_amount) || 0;
			const dnAmt = this.includeDebitNote ? (Number(row.dn_amount) || 0) : 0;
			const cnAmt = this.includeCreditNote ? (Number(row.cn_amount) || 0) : 0;
			sales = ivAmt + dnAmt - cnAmt;
			customersCount = this.includeDebitNote
				? (Number(row.iv_dn_customer_count) || 0)
				: (Number(row.iv_customer_count) || 0);
		}

		this.customersCount = customersCount;
		this.totalSales = addComma(sales, 0, false);
		this.salesPerCustomer = addComma(
			customersCount !== 0 ? sales / customersCount : 0,
			0,
			false,
		);

		this.generateLoadingText(1);
		this.saveHistory('customersCount', 'totalSales', 'salesPerCustomer');
	}
}
